import React, { useMemo } from 'react';
import { TeamInfo } from '../TeamInfo';
import { ColumnConfig, SortMode } from '../types';
import { DriverTimeStatus } from './DriverTimeStatus';
import { Sparkline } from './charts/Sparkline';
import './TeamGrid.css';

interface TeamGridProps {
  teams: Map<string, TeamInfo>;
  sortMode: SortMode;
  visibleColumns: ColumnConfig[];
  highlightedTeamIds: Set<string>;
  onToggleHighlight: (teamId: string) => void;
  /** Double-click a row to open stint / lap detail. */
  onOpenTeamDetail?: (teamId: string) => void;
  searchQuery?: string;
  /** Penalty/warning count per kart number, for the ⚠ badge on the team cell. */
  penaltyCountByKart?: Map<string, number>;
  /** Click the ⚠ badge to open the penalty-reasons popup for that team. */
  onOpenPenalties?: (teamId: string) => void;
}

/** Single-column display: `Team (driver)` when both exist; otherwise team or driver only. */
function formatTeamAndDriver(team: TeamInfo): string {
  const name = team.teamName?.trim() ?? '';
  const driver = team.driver?.trim() ?? '';
  if (name && driver) {
    if (name.toLowerCase() === driver.toLowerCase()) {
      return name;
    }
    return `${name} (${driver})`;
  }
  if (name) return name;
  if (driver) return driver;
  return '—';
}

function teamMatchesQuery(team: TeamInfo, needle: string): boolean {
  if (!needle) return true;
  const q = needle.toLowerCase().trim();
  if (!q) return true;
  const haystack = [
    team.id,
    team.kartNumber,
    team.teamName,
    team.driver,
    team.category,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((token) => haystack.includes(token));
}

type CategoryBucket = 'pro' | 'gold' | 'silver' | 'other';

const CATEGORY_GROUP_ORDER: CategoryBucket[] = ['pro', 'gold', 'silver', 'other'];

function bucketCategory(team: TeamInfo): CategoryBucket {
  const c = (team.category || '').trim().toLowerCase();
  if (c === 'pro' || c === 'gold' || c === 'silver') return c;
  return 'other';
}

function categoryGroupHeading(key: CategoryBucket): string {
  switch (key) {
    case 'pro':
      return '🏆 Pro';
    case 'gold':
      return '🥇 Gold';
    case 'silver':
      return '🥈 Silver';
    case 'other':
      return 'Other';
    default:
      return key;
  }
}

export const TeamGrid: React.FC<TeamGridProps> = ({
  teams,
  sortMode,
  visibleColumns,
  highlightedTeamIds,
  onToggleHighlight,
  onOpenTeamDetail,
  searchQuery = '',
  penaltyCountByKart,
  onOpenPenalties,
}) => {

  const computeBestAvg = (pick: (t: TeamInfo) => string): number | null => {
    let best: number | null = null;
    Array.from(teams.values()).forEach((team) => {
      const avg = pick(team);
      if (!avg) return;
      const seconds = team.convertLapTimeToSeconds(avg);
      if (seconds === null) return;
      if (best === null || seconds < best) best = seconds;
    });
    return best;
  };

  const bestAvg5Seconds = useMemo<number | null>(
    () => computeBestAvg((t) => t.getAverageLapTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams],
  );

  const bestAvg10Seconds = useMemo<number | null>(
    () => computeBestAvg((t) => t.getAverageLapTime10()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams],
  );

  const formatGapFrom = (
    team: TeamInfo,
    avgStr: string,
    bestSeconds: number | null,
  ): string => {
    if (bestSeconds === null) return '-';
    if (!avgStr) return '-';
    const seconds = team.convertLapTimeToSeconds(avgStr);
    if (seconds === null) return '-';
    const delta = seconds - bestSeconds;
    if (delta <= 0.0005) return '+0.000';
    return `+${delta.toFixed(3)}`;
  };

  const sortedTeams = useMemo(() => {
    const teamArray = Array.from(teams.values());

    const compareByAvg = (
      a: TeamInfo,
      b: TeamInfo,
      pick: (t: TeamInfo) => string,
    ): number => {
      const avgA = pick(a);
      const avgB = pick(b);
      if (!avgA && !avgB) return 0;
      if (!avgA) return 1;
      if (!avgB) return -1;
      const timeA = a.convertLapTimeToSeconds(avgA);
      const timeB = b.convertLapTimeToSeconds(avgB);
      if (timeA === null && timeB === null) return 0;
      if (timeA === null) return 1;
      if (timeB === null) return -1;
      return timeA - timeB;
    };

    if (sortMode === 'average') {
      return teamArray.sort((a, b) =>
        compareByAvg(a, b, (t) => t.getAverageLapTime()),
      );
    } else if (sortMode === 'average10') {
      return teamArray.sort((a, b) =>
        compareByAvg(a, b, (t) => t.getAverageLapTime10()),
      );
    } else if (sortMode === 'best') {
      return teamArray.sort((a, b) => {
        const bestA = a.convertLapTimeToSeconds(a.bestLap);
        const bestB = b.convertLapTimeToSeconds(b.bestLap);
        if (bestA === null && bestB === null) return 0;
        if (bestA === null) return 1;
        if (bestB === null) return -1;
        return bestA - bestB;
      });
    } else if (sortMode === 'category') {
      return teamArray.sort((a, b) => {
        const categoryA = a.category || '';
        const categoryB = b.category || '';
        const categoryOrder = { pro: 0, gold: 1, silver: 2, '': 3 };
        const orderA = categoryOrder[categoryA.toLowerCase() as keyof typeof categoryOrder] ?? 3;
        const orderB = categoryOrder[categoryB.toLowerCase() as keyof typeof categoryOrder] ?? 3;
        if (orderA !== orderB) return orderA - orderB;
        return compareByAvg(a, b, (t) => t.getAverageLapTime());
      });
    } else {
      return teamArray.sort((a, b) => {
        const posA = parseInt(a.position || '999');
        const posB = parseInt(b.position || '999');
        return posA - posB;
      });
    }
  }, [teams, sortMode]);

  const visibleTeams = useMemo(
    () => sortedTeams.filter((t) => teamMatchesQuery(t, searchQuery)),
    [sortedTeams, searchQuery],
  );

  const categoryGroups = useMemo(() => {
    if (sortMode !== 'category' || visibleTeams.length === 0) return null;
    const buckets: Record<CategoryBucket, TeamInfo[]> = {
      pro: [],
      gold: [],
      silver: [],
      other: [],
    };
    for (const t of visibleTeams) {
      buckets[bucketCategory(t)].push(t);
    }
    return CATEGORY_GROUP_ORDER.filter((k) => buckets[k].length > 0).map((k) => ({
      key: k,
      heading: categoryGroupHeading(k),
      teams: buckets[k],
    }));
  }, [sortMode, visibleTeams]);

  const getDriverClass = (driverName: string): string => {
    const name = driverName.toLowerCase();
    if (name.includes('zr racing') || name.includes('ora racing')) return 'driver-highlight';
    return '';
  };

  const getCellValue = (team: TeamInfo, columnKey: string, teamIndex?: number): React.ReactNode => {
    switch (columnKey) {
      case 'currentPosition': return teamIndex !== undefined ? (teamIndex + 1).toString() : '-';
      case 'position':        return team.position || '-';
      case 'kartNumber':      return team.kartNumber || '-';
      case 'team':            return team.id;
      case 'teamName': {
        const penalties = penaltyCountByKart?.get(team.kartNumber) ?? 0;
        return (
          <>
            <span>{formatTeamAndDriver(team)}</span>
            <DriverTimeStatus team={team} />
            {penalties > 0 && (
              <button
                type="button"
                className="penalty-badge"
                title={`${penalties} penalty/warning${penalties > 1 ? 's' : ''} — click for details`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenPenalties?.(team.id);
                }}
              >
                ⚠ {penalties}
              </button>
            )}
            {/* Stints are otherwise only reachable by double-click on the row,
                which a touchscreen gives up to zoom and text selection. Shown
                by CSS on touch only, so the mouse layout is unchanged. */}
            {onOpenTeamDetail && (
              <button
                type="button"
                className="team-detail-btn"
                title={`Stints for ${team.id}`}
                aria-label={`Show stints for ${team.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTeamDetail(team.id);
                }}
              >
                ⋯
              </button>
            )}
          </>
        );
      }
      case 'category': {
        const cat = (team.category || '').trim();
        if (!cat) return '—';
        const variant = cat.toLowerCase();
        const label =
          variant === 'gold' ? 'Gold' :
          variant === 'silver' ? 'Silver' :
          variant === 'pro' ? 'Pro' : cat;
        if (variant !== 'gold' && variant !== 'silver' && variant !== 'pro') {
          return label;
        }
        return (
          <span className={`category-flag category-flag--${variant}`}>
            {label}
          </span>
        );
      }
      case 'lastLap':     return team.lastLap || '-';
      case 'bestLap':     return team.bestLap || '-';
      case 'avgLap':      return team.getAverageLapTime() || '-';
      case 'avgLap10':    return team.getAverageLapTime10() || '-';
      case 'avgGap':      return formatGapFrom(team, team.getAverageLapTime(), bestAvg5Seconds);
      case 'avgGap10':        return formatGapFrom(team, team.getAverageLapTime10(), bestAvg10Seconds);
      case 'consistency5':    return team.getConsistency5() || '-';
      case 'consistency10':   return team.getConsistency10() || '-';
      case 'cleanAvg':        return team.getCleanAverageLap() || '-';
      case 'paceTrend': {
        // lastLapTimes is newest-first; a trace reads left-to-right in time.
        const recent = [...team.lastLapTimes].reverse();
        return (
          <Sparkline
            values={recent}
            title={`Last ${recent.length} laps for kart ${team.kartNumber || '—'}`}
          />
        );
      }
      case 'consistency': {
        const r = team.getConsistencyRating();
        if (!r) return '-';
        const labelText = r.label.charAt(0).toUpperCase() + r.label.slice(1);
        return (
          <span
            className={`consistency-rating consistency-rating--${r.label}`}
            title={`Score ${r.score}/100 · CV ${(r.cv * 100).toFixed(2)}%`}
          >
            {labelText}
          </span>
        );
      }
      case 'gap':         return team.gap || '-';
      case 'interval':    return team.interval || '-';
      case 'sectors':     return `${team.sector1 || '-'}/${team.sector2 || '-'}/${team.sector3 || '-'}`;
      case 'onTrackTime': return team.onTrackTime || '-';
      case 'pitCount':    return team.pitCount || '0';
      case 'boxStatus':   return team.inBox ? `IN (${team.boxTimeRemaining})` : 'OUT';
      case 'stintAvgs': {
        const rows = team.stintAverages;
        if (!rows?.length) return '—';
        return rows
          .map((r) => {
            const name = r.driver.trim();
            const short =
              name.length > 16 ? `${name.slice(0, 14)}…` : name || '—';
            const mark = r.kind === 'current' ? ' *' : '';
            return `${short} ${r.averageLap} (${r.lapCount}L)${mark}`;
          })
          .join(' · ');
      }
      case 'driverAvgs': {
        const rows = team.driverSessionAverages;
        if (!rows?.length) return '—';
        return rows
          .map((r) => {
            const name = r.driver.trim();
            const short =
              name.length > 16 ? `${name.slice(0, 14)}…` : name || '—';
            return `${short}: ${r.averageLap} (${r.lapCount}L)`;
          })
          .join(' · ');
      }
      default: return '-';
    }
  };

  const getCellClasses = (team: TeamInfo, columnKey: string): string => {
    const classes: string[] = ['grid-cell'];

    const trackableFields = ['position', 'kartNumber', 'teamName', 'lastLap', 'bestLap', 'gap', 'interval', 'onTrackTime', 'pitCount'];
    const teamColChanged =
      columnKey === 'teamName' &&
      (team.hasChanged('teamName') || team.hasChanged('driver'));
    if (
      (columnKey === 'teamName' && teamColChanged) ||
      (columnKey !== 'teamName' && trackableFields.includes(columnKey) && team.hasChanged(columnKey))
    ) {
      classes.push('field-changed');
    }

    if (columnKey === 'teamName') {
      const dc = getDriverClass(team.driver);
      if (dc) classes.push(dc);
    }

    if (columnKey === 'boxStatus') classes.push(team.inBox ? 'box-in' : 'box-out');
    if (columnKey === 'currentPosition') classes.push('current-position');
    if (columnKey === 'category') classes.push('category-cell');
    if (columnKey === 'stintAvgs' || columnKey === 'driverAvgs') {
      classes.push('stint-summary-cell');
    }

    const timeColumns = ['lastLap', 'bestLap', 'avgLap', 'avgLap10', 'avgGap', 'avgGap10', 'gap', 'interval', 'sectors', 'onTrackTime', 'consistency5', 'consistency10', 'cleanAvg'];
    if (timeColumns.includes(columnKey)) classes.push('time-cell');

    return classes.join(' ');
  };

  if (sortedTeams.length === 0) {
    return (
      <div className="team-grid">
        <div className="no-data">No team data. Connect to a track and press Start.</div>
      </div>
    );
  }

  if (visibleTeams.length === 0) {
    return (
      <div className="team-grid">
        <div className="grid-header">
          <h3>
            {sortMode === 'position'  ? 'By Position' :
             sortMode === 'average'   ? 'By Avg Lap (5)' :
             sortMode === 'average10' ? 'By Avg Lap (10)' :
             sortMode === 'best'      ? 'By Best Lap' :
                                        'By Category (grouped)'}
          </h3>
          <div className="team-count">0 / {sortedTeams.length} teams</div>
        </div>
        <div className="no-data">No teams match "{searchQuery}".</div>
      </div>
    );
  }

  return (
    <div className="team-grid">
      <div className="grid-header">
        <h3>
          {sortMode === 'position'  ? 'By Position' :
           sortMode === 'average'   ? 'By Avg Lap (5)' :
           sortMode === 'average10' ? 'By Avg Lap (10)' :
           sortMode === 'best'      ? 'By Best Lap' :
                                      'By Category (grouped)'}
        </h3>
        <div className="team-count">
          {searchQuery
            ? `${visibleTeams.length} / ${sortedTeams.length} teams`
            : `${sortedTeams.length} teams`}
        </div>
      </div>

      <div className="grid-scroll-wrapper">
        <table className="grid-table">
          <thead>
            <tr className="grid-header-row">
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`grid-cell header-cell${col.key === 'currentPosition' ? ' sticky-col' : ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortMode === 'category' && categoryGroups
              ? (() => {
                  let displayIndex = 0;
                  const nodes: React.ReactNode[] = [];
                  for (const group of categoryGroups) {
                    nodes.push(
                      <tr key={`cat-${group.key}`} className="category-group-header">
                        <td colSpan={visibleColumns.length}>{group.heading}</td>
                      </tr>,
                    );
                    for (const team of group.teams) {
                      const rowIndex = displayIndex++;
                      const isHighlighted = highlightedTeamIds.has(team.id);
                      nodes.push(
                        <tr
                          key={team.id}
                          className={`grid-row${isHighlighted ? ' highlighted-row' : ''}`}
                          onClick={() => onToggleHighlight(team.id)}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            onOpenTeamDetail?.(team.id);
                          }}
                        >
                          {visibleColumns.map((col) => (
                            <td
                              key={col.key}
                              className={`${getCellClasses(team, col.key)}${col.key === 'currentPosition' ? ' sticky-col' : ''}`}
                            >
                              {getCellValue(team, col.key, rowIndex)}
                            </td>
                          ))}
                        </tr>,
                      );
                    }
                  }
                  return nodes;
                })()
              : visibleTeams.map((team, index) => {
                  const isHighlighted = highlightedTeamIds.has(team.id);
                  return (
                    <tr
                      key={team.id}
                      className={`grid-row${isHighlighted ? ' highlighted-row' : ''}`}
                      onClick={() => onToggleHighlight(team.id)}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        onOpenTeamDetail?.(team.id);
                      }}
                    >
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className={`${getCellClasses(team, col.key)}${col.key === 'currentPosition' ? ' sticky-col' : ''}`}
                        >
                          {getCellValue(team, col.key, index)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
